import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';
import { AppFacadeService } from '../../../services/app-facade.service';
import { FeatureToggleService } from '../../../services/feature-toggle.service';
import { VersionToggleService } from '../../../services/version-toggle';
import { Redirection } from '../../../types/redirection.type';
import { ConsumerPortalState } from '../../../types/state.type';
import { selectIsConfigSet } from '../redirection/redirection.selectors';
import { loadVersionRedirection, loadVersionRedirectionFailure, setActiveTab, setConsumerPortalState, setVersionRedirection } from './app.actions';
import { clearContractDetailInStore } from '../../domains/contract/contract.actions';
import { AppEventService } from '../../../services/app-event.service';
import { OriginActionName } from '@por/shared/ui/cross-app';
import { selectConfig, selectCustomerId } from '../config/config.selectors';

@Injectable()
export class AppEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly versionToggleService = inject(VersionToggleService);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly translateService = inject(TranslateService);
    private readonly appEventService = inject(AppEventService);

    private readonly appViewState$ = createEffect(() =>
        combineLatest([this.appFacadeService.getAuthenticated(), this.appFacadeService.getRedirected(), this.store.select(selectIsConfigSet)]).pipe(
            distinctUntilChanged(),
            map(([isAuthenticated, redirected, config]) => {
                let consumerPortalState: ConsumerPortalState = 'None';
                if (config && isAuthenticated === false) {
                    if (this.versionToggleService.isQuickLinkVersion()) {
                        consumerPortalState = 'redirected';
                    } else {
                        if (this.featureToggleService.isAvailable('consumerLogin') === true) {
                            consumerPortalState = 'login';
                        } else {
                            consumerPortalState = 'embeded-login';
                        }
                    }
                } else if (config && isAuthenticated === true) {
                    if (this.versionToggleService.isQuickLinkVersion()) {
                        consumerPortalState = 'portal';
                    } else {
                        if (redirected) {
                            consumerPortalState = 'portal';
                        } else {
                            consumerPortalState = 'redirected';
                        }
                    }
                }
                if (!isAuthenticated) {
                    if (redirected === true) {
                        this.appFacadeService.setRedirected(false);
                    }
                    this.appFacadeService.setLoading(false);
                }

                return setConsumerPortalState({ consumerPortalState: consumerPortalState });
            })
        )
    );

    /**
     * Dispatch ComponentReady cross-app event when the portal is fully initialized.
     * This ensures all initialization (auth, config, routing) is complete before
     * external apps (e.g., Storefront) receive the ComponentReady signal and
     * potentially trigger actions like QuoteSubmit.
     */
    private readonly componentReady$ = createEffect(
        () =>
            this.appFacadeService.getStates().pipe(
                filter(state => state === 'portal'),
                take(1),
                withLatestFrom(this.store.select(selectConfig), this.store.select(selectCustomerId)),
                tap(([_, config, customerId]) => {
                    this.appEventService.dispatch({
                        actionName: OriginActionName[OriginActionName.ComponentReady],
                        data: {
                            organizationId: config?.organizationId,
                            customerId: customerId
                        }
                    });
                })
            ),
        { dispatch: false }
    );

    private readonly loadVersionRedirection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadVersionRedirection),
            tap(() => {
                this.appFacadeService.setLoading(true);
            }),
            switchMap(action =>
                this.getComponentVersion(action.orgId).pipe(
                    map((redirectionVersion: Redirection) => {
                        this.appFacadeService.setLoading(false);
                        return setVersionRedirection({ redirectionVersion });
                    }),
                    catchError(error => {
                        this.appFacadeService.setLoading(false);
                        return of(loadVersionRedirectionFailure({ error: error.message }));
                    })
                )
            )
        )
    );

    private readonly activeTabChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setActiveTab),
                tap(action => {
                    this.appFacadeService.showManageUserDialogDisplayed(false); // side-effect only
                    if (action.clearSelectedContractId) {
                        this.store.dispatch(clearContractDetailInStore());
                    }
                })
            ),
        { dispatch: false }
    );

    private getComponentVersion(orgId: string) {
        return this.versionToggleService.getOrgVersion(orgId).pipe(
            map(() => {
                const toLoad = this.determineComponentToLoad();
                return { toLoad, orgId };
            }),
            catchError(e => {
                let errorMessage = e?.error?.message;
                let toLoad: ComponentsToLoad = ComponentsToLoad.ConsumerPortalError;

                if (errorMessage.includes('OrganizationIamCredsNotFound')) {
                    if (this.versionToggleService.isQuickLinkVersion()) {
                        errorMessage = this.translateService.instant('OrganizationIamCredsNotFound');
                    } else {
                        errorMessage = '';
                        toLoad = ComponentsToLoad.BasicConsumerPortal;
                    }
                }

                return of({
                    toLoad,
                    orgId
                });
            })
        );
    }

    private determineComponentToLoad(): ComponentsToLoad {
        if (this.versionToggleService.isConsumerQuickLinkVersion() && this.versionToggleService.isAdvanceVersion()) {
            return ComponentsToLoad.ConsumerPortal;
        }
        if (this.versionToggleService.isQuickLinkVersion() && !this.versionToggleService.isConsumerQuickLinkVersion()) {
            return ComponentsToLoad.QuickLink;
        }
        if (this.versionToggleService.isAdvanceVersion()) {
            return ComponentsToLoad.ConsumerPortal;
        }
        return ComponentsToLoad.BasicConsumerPortal;
    }
}
